import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { inject, observer } from 'mobx-react'
import { withRouter } from 'react-router-dom'
import { formatDateBrazil, openNotificationLink } from 'utils'
import { IconAction, font } from './accountMenu.styles'

const getNotificationAppearance = (type) => {
  switch (String(type || 'info').toLowerCase()) {
    case 'error':
      return {
        accent: '#ef4444',
        softBackground: '#fff5f5',
        hoverBackground: '#fff1f2',
        dot: '#ef4444',
        badgeBackground: '#fee2e2',
        badgeColor: '#b91c1c',
        badgeLabel: 'Erro',
      }
    case 'warning':
      return {
        accent: '#f59e0b',
        softBackground: '#fffaf0',
        hoverBackground: '#fffbeb',
        dot: '#f59e0b',
        badgeBackground: '#fef3c7',
        badgeColor: '#b45309',
        badgeLabel: 'Aviso',
      }
    default:
      return {
        accent: '#1a56db',
        softBackground: '#fafbff',
        hoverBackground: '#f4f8ff',
        dot: '#1a56db',
        badgeBackground: '#dbeafe',
        badgeColor: '#1d4ed8',
        badgeLabel: 'Info',
      }
  }
}

const NotificationsWrapper = styled.div`
  position: relative;
  margin-right: 4px;
`

const BellButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  position: relative;
  height: 36px;
  width: 36px;
  border-radius: 10px;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.4);
  }
`

const UnreadBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #2962ff;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #4a203e;
`

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 400px;
  max-width: min(400px, calc(100vw - 16px));
  background: #fff;
  border: 1px solid #ececf0;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(9, 9, 11, 0.14), 0 2px 6px rgba(9, 9, 11, 0.06);
  overflow: hidden;
  z-index: 1200;
  font-family: ${font};

  /* no celular ocupa a largura da tela, abaixo da barra */
  @media (max-width: 767px) {
    position: fixed;
    top: 62px;
    left: 8px;
    right: 8px;
    width: auto;
    max-width: none;
  }
`

const Header = styled.div`
  padding: 12px 10px 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`

const Title = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #18181b;
  flex-shrink: 0;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const FiltersBar = styled.div`
  margin: 0 12px 10px;
  padding: 3px;
  display: flex;
  gap: 2px;
  border-radius: 10px;
  background: #f4f4f5;
`

const FilterButton = styled.button`
  flex: 1;
  background: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  border: 0;
  border-radius: 8px;
  padding: 6px 8px;
  font-family: ${font};
  font-size: 12.5px;
  color: ${({ active }) => (active ? '#18181b' : '#71717a')};
  font-weight: ${({ active }) => (active ? 700 : 500)};
  box-shadow: ${({ active }) => (active ? '0 1px 2px rgba(9, 9, 11, 0.08)' : 'none')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    color: #18181b;
  }

  .count {
    margin-left: 5px;
    color: #a1a1aa;
    font-weight: 600;
  }
`

const List = styled.div`
  max-height: 380px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 2px;
  }
`

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
`

const EmptyText = styled.div`
  font-size: 13px;
  color: #94a3b8;
`

const NotificationItem = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #f4f4f5;
  position: relative;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  transition: background 0.1s;
  background: ${({ unread, notificationType }) =>
    unread ? getNotificationAppearance(notificationType).softBackground : '#fff'};

  &:hover {
    background: #fafafa;
  }

  ${({ unread, notificationType }) =>
    unread
      ? `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: ${getNotificationAppearance(notificationType).accent};
      border-radius: 0 2px 2px 0;
    }
  `
      : ''}
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ read, notificationType }) =>
    read ? '#e2e8f0' : getNotificationAppearance(notificationType).dot};
  flex-shrink: 0;
  margin-top: 5px;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const TopMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3px;
`

const ItemTitle = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #18181b;
  line-height: 1.3;
`

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${({ notificationType }) =>
    getNotificationAppearance(notificationType).badgeBackground};
  color: ${({ notificationType }) => getNotificationAppearance(notificationType).badgeColor};
`

const ItemTime = styled.span`
  font-size: 10px;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 1px;
`

const Description = styled.div`
  font-size: 12.5px;
  color: #52525b;
  line-height: 1.4;
  margin-bottom: 6px;
`

const Path = styled.div`
  font-size: 11.5px;
  color: ${({ notificationType }) => getNotificationAppearance(notificationType).badgeColor};
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;

  i {
    margin-right: 5px;
    font-size: 10px;
  }
`

const ItemActions = styled.div`
  position: absolute;
  right: 12px;
  bottom: 10px;
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 9px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(9, 9, 11, 0.1);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;

  ${NotificationItem}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`

const ActionButton = styled.button`
  background: none;
  border: 0.5px solid
    ${({ variant }) => {
      if (variant === 'read') return '#bfdbfe'
      if (variant === 'delete') return '#fecaca'
      return '#e2e8f0'
    }};
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  color: ${({ variant }) => {
    if (variant === 'read') return '#1a56db'
    if (variant === 'delete') return '#ef4444'
    return '#0f172a'
  }};

  &:hover {
    background: ${({ variant }) => {
      if (variant === 'read') return '#eff6ff'
      if (variant === 'delete') return '#fef2f2'
      return '#f1f5f9'
    }};
  }
`

const Footer = styled.div`
  padding: 8px 10px 8px 16px;
  border-top: 1px solid #ececf0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FooterCount = styled.span`
  font-size: 11px;
  color: #94a3b8;
`

const RefreshButton = styled.button`
  background: none;
  border: none;
  font-family: ${font};
  font-size: 12px;
  color: #71717a;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.12s;

  &:hover {
    background: #f4f4f5;
    color: #18181b;
  }
`

const Notifications = ({ rootStore, history }) => {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('all')
  const wrapperRef = useRef(null)
  const { notificationStore } = rootStore

  useEffect(() => {
    notificationStore.loadNotifications()
  }, [notificationStore])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current || wrapperRef.current.contains(event.target)) return
      setOpen(false)
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const allNotifications = notificationStore.notifications

  const unreadNotifications = useMemo(
    () => allNotifications.filter((notification) => !notification.isRead),
    [allNotifications]
  )

  const readNotifications = useMemo(
    () => allNotifications.filter((notification) => notification.isRead),
    [allNotifications]
  )

  const notifications = useMemo(() => {
    if (filter === 'unread') return unreadNotifications
    if (filter === 'read') return readNotifications
    return allNotifications
  }, [filter, allNotifications, unreadNotifications, readNotifications])

  const markVisibleAsRead = async () => {
    const visibleUnread = notifications.filter((notification) => !notification.isRead)
    if (visibleUnread.length === 0) return
    await notificationStore.markAllAsRead(visibleUnread)
  }

  const deleteVisible = async () => {
    if (notifications.length === 0) return
    await notificationStore.removeMany(notifications)
    if (filter !== 'all') setFilter('all')
  }

  const handleRefresh = async () => {
    await notificationStore.loadNotifications(true)
  }

  const handleMarkAsRead = async (event, notification) => {
    event.stopPropagation()
    if (notification.isRead) return
    await notificationStore.markAsRead(notification.id)
  }

  const handleDelete = async (event, notification) => {
    event.stopPropagation()
    await notificationStore.remove(notification.id)
  }

  const handleOpen = async (event, notification) => {
    event.stopPropagation()
    if (!notification.isRead) await notificationStore.markAsRead(notification.id)
    setOpen(false)
    openNotificationLink(notification.actionLink || '/notifications', history)
  }

  return (
    <NotificationsWrapper ref={wrapperRef}>
      <BellButton type="button" onClick={() => setOpen(!open)} aria-label="Notifications">
        <i className={open ? 'fas fa-bell' : 'far fa-bell'} />
        {notificationStore.unreadCount > 0 && (
          <UnreadBadge>
            {notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount}
          </UnreadBadge>
        )}
      </BellButton>

      {open && (
        <DropdownPanel>
          <Header>
            <Title>Notificações</Title>
            <HeaderActions>
              <IconAction
                type="button"
                title="Marcar todas como lidas"
                aria-label="Marcar todas como lidas"
                disabled={unreadNotifications.length === 0}
                onClick={markVisibleAsRead}
              >
                <i className="fas fa-check-double" />
              </IconAction>
              <IconAction
                type="button"
                danger
                title="Apagar todas"
                aria-label="Apagar todas"
                disabled={allNotifications.length === 0}
                onClick={deleteVisible}
              >
                <i className="far fa-trash-alt" />
              </IconAction>
              <IconAction
                type="button"
                title="Fechar"
                aria-label="Fechar"
                onClick={() => setOpen(false)}
              >
                <i className="fas fa-times" />
              </IconAction>
            </HeaderActions>
          </Header>

          <FiltersBar>
            <FilterButton type="button" active={filter === 'all'} onClick={() => setFilter('all')}>
              Todas<span className="count">{allNotifications.length}</span>
            </FilterButton>
            <FilterButton
              type="button"
              active={filter === 'unread'}
              onClick={() => setFilter('unread')}
            >
              Não lidas<span className="count">{unreadNotifications.length}</span>
            </FilterButton>
            <FilterButton
              type="button"
              active={filter === 'read'}
              onClick={() => setFilter('read')}
            >
              Lidas<span className="count">{readNotifications.length}</span>
            </FilterButton>
          </FiltersBar>

          <List>
            {notifications.length === 0 && (
              <EmptyState>
                <EmptyText>Nenhuma notificação encontrada.</EmptyText>
              </EmptyState>
            )}

            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                unread={!notification.isRead}
                notificationType={notification.type}
              >
                <Dot read={notification.isRead} notificationType={notification.type} />
                <Body>
                  <TopRow>
                    <TopMeta>
                      <TypeBadge notificationType={notification.type}>
                        {getNotificationAppearance(notification.type).badgeLabel}
                      </TypeBadge>
                      <ItemTitle>{notification.title}</ItemTitle>
                    </TopMeta>
                    <ItemTime>{formatDateBrazil(notification.createdDate)}</ItemTime>
                  </TopRow>

                  <Description>{notification.description}</Description>

                  {notification.actionLink && (
                    <Path notificationType={notification.type} title={notification.actionLink}>
                      <i className="fas fa-link" aria-hidden="true" />
                      {notification.actionLink}
                    </Path>
                  )}

                  <ItemActions>
                    {!notification.isRead && (
                      <ActionButton
                        type="button"
                        variant="read"
                        onClick={(event) => handleMarkAsRead(event, notification)}
                      >
                        Marcar como lida
                      </ActionButton>
                    )}
                    <ActionButton
                      type="button"
                      variant="open"
                      onClick={(event) => handleOpen(event, notification)}
                    >
                      Abrir
                    </ActionButton>
                    <ActionButton
                      type="button"
                      variant="delete"
                      onClick={(event) => handleDelete(event, notification)}
                    >
                      Excluir
                    </ActionButton>
                  </ItemActions>
                </Body>
              </NotificationItem>
            ))}
          </List>

          <Footer>
            <FooterCount>
              {allNotifications.length} notifica{allNotifications.length === 1 ? 'ção' : 'ções'}
            </FooterCount>
            <RefreshButton type="button" onClick={handleRefresh}>
              <i className="fas fa-sync-alt" style={{ marginRight: 6, fontSize: 11 }} />
              Atualizar
            </RefreshButton>
          </Footer>
        </DropdownPanel>
      )}
    </NotificationsWrapper>
  )
}

export default withRouter(inject('rootStore')(observer(Notifications)))
